import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { UnitTransferReasonEnumType, UnitTransferScopeEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Alert from 'primitives/Alert';
import LoadingIndicator from 'primitives/LoadingIndicator';

import { fixToTwoLocalPrice } from 'utils/format-helper';

const MY_INVESTMENT_ENTITIES_QUERY = gql(`
  query MyInvestmentEntitiesForUnitTransfer {
    dashboard {
      id
      investmentEntities {
        id
        name
        type
      }
    }
  }
`);

const TRANSFERABLE_SCHEMES_QUERY = gql(`
  query TransferableUnitTransferSchemes($investmentEntityId: ID!) {
    transferableUnitTransferSchemes(investmentEntityId: $investmentEntityId) {
      id
      schemeName
      isin
      numberOfUnits
      investmentAmount
    }
  }
`);

export type ScopeStepData = {
  transferorInvestmentEntityId: string;
  transferorInvestmentEntityName: string;
  reason: UnitTransferReasonEnumType | '';
  reasonDescription: string;
  relationship: string;
  scope: UnitTransferScopeEnumType | '';
  // Closing ids of the schemes picked when scope is ALL_UNITS_SELECTED_SCHEMES; empty otherwise.
  closingIds: string[];
};

const StepScope: FC<{
  data: ScopeStepData;
  onSubmit: (data: ScopeStepData) => void;
  buttonRef: any;
}> = ({ data, onSubmit, buttonRef }) => {
  const { data: entitiesData, loading, error, refetch } = useQuery(MY_INVESTMENT_ENTITIES_QUERY);
  const [pickedEntityId, setPickedEntityId] = useState(data.transferorInvestmentEntityId);
  const [pickedScope, setPickedScope] = useState(data.scope);
  const [emptySelectionError, setEmptySelectionError] = useState(false);

  const isSelectedSchemesScope = pickedScope === UnitTransferScopeEnumType.AllUnitsSelectedSchemes;
  const {
    data: schemesData,
    loading: schemesLoading,
    error: schemesError,
  } = useQuery(TRANSFERABLE_SCHEMES_QUERY, {
    variables: { investmentEntityId: pickedEntityId },
    skip: !isSelectedSchemesScope || !pickedEntityId,
  });
  const transferableSchemes = schemesData?.transferableUnitTransferSchemes ?? [];

  if (loading) return <LoadingIndicator />;

  if (error || !entitiesData) return <ErrorMessage error={error} refetch={refetch} />;

  const investmentEntities = entitiesData.dashboard.investmentEntities;

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Unit transfers are permitted only for bonafide reasons such as death, disability, or entity
        restructuring, and must be complete transfers — partial transfers within a scheme are not
        allowed.
      </p>
      <FormPanel
        loading={false}
        error={undefined}
        onSubmit={formData => {
          // The checkbox state can still hold ids from a previously chosen entity, so keep only
          // schemes that belong to the entity being submitted.
          const closingIds = isSelectedSchemesScope
            ? (formData.closingIds ?? []).filter((id: string) =>
                transferableSchemes.some(scheme => scheme.id === id)
              )
            : [];

          if (isSelectedSchemesScope && closingIds.length === 0) {
            setEmptySelectionError(true);
            return;
          }

          setEmptySelectionError(false);
          onSubmit({
            ...formData,
            closingIds,
            transferorInvestmentEntityName:
              investmentEntities.find(entity => entity.id === formData.transferorInvestmentEntityId)
                ?.name ?? '',
          });
        }}
        buttonRef={buttonRef}
      >
        <FormInput
          fullWidth
          type="select"
          fieldName="transferorInvestmentEntityId"
          label="Transferring from *"
          defaultValue={data.transferorInvestmentEntityId}
          onChange={formData => setPickedEntityId(formData.transferorInvestmentEntityId)}
          options={investmentEntities.map(entity => ({
            label: entity.name,
            value: entity.id,
          }))}
          validators={{ required: true }}
        />
        <FormInput
          fullWidth
          type="select"
          fieldName="reason"
          label="Reason for transfer *"
          defaultValue={data.reason}
          options={[
            { label: 'Death of the investor', value: UnitTransferReasonEnumType.Death },
            { label: 'Disability of the investor', value: UnitTransferReasonEnumType.Disability },
            {
              label: 'Entity restructuring',
              value: UnitTransferReasonEnumType.EntityRestructuring,
            },
            {
              label: 'Family office restructuring',
              value: UnitTransferReasonEnumType.FamilyOfficeRestructuring,
            },
            { label: 'Sale of units', value: UnitTransferReasonEnumType.SaleOfUnits },
            { label: 'Other', value: UnitTransferReasonEnumType.Other },
          ]}
          validators={{ required: true }}
        />
        <FormInput
          fullWidth
          type="textarea"
          fieldName="reasonDescription"
          label="Describe the reason"
          helperText="Provide any additional context ops may need to review this request"
          defaultValue={data.reasonDescription}
          validators={{ required: false }}
        />
        <FormInput
          fullWidth
          type="text"
          fieldName="relationship"
          label="Relationship to the transferee"
          helperText="e.g. spouse, son, daughter, legal heir"
          defaultValue={data.relationship}
          validators={{ required: false }}
        />
        <FormInput
          fullWidth
          type="radio-group"
          fieldName="scope"
          label="Scope of transfer *"
          defaultValue={data.scope}
          options={[
            {
              label: 'All units across all schemes',
              value: UnitTransferScopeEnumType.AllUnitsAllSchemes,
            },
            {
              label: 'All units in selected schemes',
              value: UnitTransferScopeEnumType.AllUnitsSelectedSchemes,
            },
          ]}
          onChange={formData => setPickedScope(formData.scope)}
          validators={{ required: true }}
        />
        <FormInput
          fullWidth
          type="checkbox-group"
          fieldName="closingIds"
          label="Schemes to transfer *"
          helperText={
            schemesLoading
              ? 'Loading your schemes…'
              : schemesError
              ? 'Could not load your schemes. Please go back and try again.'
              : transferableSchemes.length === 0
              ? 'No transferable schemes found for this entity.'
              : 'All units in each selected scheme will be transferred.'
          }
          defaultValue={data.closingIds}
          options={transferableSchemes.map(scheme => ({
            label: `${scheme.schemeName}${scheme.isin ? ` (${scheme.isin})` : ''} — ${
              scheme.numberOfUnits
            } units, ₹ ${fixToTwoLocalPrice(scheme.investmentAmount)}`,
            value: scheme.id,
          }))}
          conditionsToShow={{
            matches: [
              {
                field: 'scope',
                condition: '===',
                value: UnitTransferScopeEnumType.AllUnitsSelectedSchemes,
              },
            ],
          }}
          validators={{ required: true }}
        />
      </FormPanel>
      {emptySelectionError && (
        <div className="mt-4">
          <Alert
            type="error"
            title="No schemes selected"
            message="Select at least one scheme that belongs to the chosen investment entity."
          />
        </div>
      )}
    </div>
  );
};

export default StepScope;
